import { AuthProvider } from "react-admin";
import { jwtDecode, JwtPayload } from "jwt-decode";
import Cookies from "js-cookie";

interface CustomJwtPayload extends JwtPayload {
  avatar?: string;
  nome_usuario?: string;
  conta?: string;
  id_empresa?: number;
  nome_reduzido?: string;
  id_predio?: number;
  sigla_loja?: string;
  logo_color?: string;
  font_color?: string;
  homologacao?: boolean;
  multi_empresa?: boolean;
  admin_scm?: boolean;
  cod_vendedor?: number;
  user_id?: number;
}

/**
 * O ambiente **não** pode depender do build (ex.: `import.meta.env.PROD`),
 * porque o mesmo bundle pode ser servido em hosts diferentes.
 * Se isso acontecer, os cookies podem "vazar" entre sandbox e produção,
 * fazendo o app ler o JWT do host errado e montar URLs erradas no menu.
 */
const hostname = typeof window !== "undefined" ? window.location.hostname : "";
export const sandboxHost =
  hostname === "localhost" || hostname.startsWith("sandbox.");
export const producao = !sandboxHost;

export const rednaxelCookie = producao ? "auth" : "auth_sandbox";
export const subdominio = producao ? "rnge" : "homolog.rnge";
export const rednaxelAPI = `https://${subdominio}.com.br/v2`;

const removeCookieEverywhere = (name: string) => {
  const base: Cookies.CookieAttributes = { path: "/" };

  // remove cookie sem domain (host-only)
  Cookies.remove(name, base);

  // remove cookie com domains comuns (para limpar resíduos de builds antigos)
  const domains = new Set<string>([
    `.${hostname}`,
    ".rnge.com.br",
    ".homolog.rnge.com.br",
  ]);
  for (const domain of domains) {
    if (!domain || domain === ".") continue;
    Cookies.remove(name, { ...base, domain });
  }
};

export const authProvider: AuthProvider = {
  login: ({ username, password }) => {
    // Construct the request options
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic " + btoa(`${username}:${password}`),
      },
      body: JSON.stringify({ username, password }),
    };

    return fetch(`${rednaxelAPI}/login`, requestOptions)
      .then((response) => {
        if (response.ok) {
          // If authentication is successful, store the JWT in a cookie
          return response.json().then((data) => {
            let opcoes: Cookies.CookieAttributes = {
              path: "/",
              secure: true,
              expires: 2,
            };
            if (producao) {
              opcoes = { ...opcoes, domain: `.${subdominio}.com.br` };
            }
            Cookies.set(rednaxelCookie, data.jwt, opcoes);
            return Promise.resolve();
          });
        } else {
          // Extract and return the error message from the response body
          return response.json().then((data) => {
            const errorMessage = data.message || "Authentication failed";
            return Promise.reject({ message: errorMessage });
          });
        }
      })
      .catch((error) => {
        // console.log("error", error);
        // Handle network or other errors
        return Promise.reject(error.message || "Authentication error");
      });

    // localStorage.setItem('username', username);
    // return Promise.resolve();
  },
  logout: () => {
    // Limpa o cookie esperado e também o "outro" para evitar vazamento entre ambientes
    // removeCookieEverywhere("auth");
    // removeCookieEverywhere("auth_sandbox");
    removeCookieEverywhere(rednaxelCookie);

    return Promise.resolve();
  },
  checkAuth: () =>
    Cookies.get(rednaxelCookie) ? Promise.resolve() : Promise.reject(),
  checkError: (error) => {
    const status = error.status;
    console.log("checkError", error);
    if (status === 401) {
      removeCookieEverywhere("auth");
      removeCookieEverywhere("auth_sandbox");
      return Promise.reject();
    }
    // other error code (404, 500, etc): no need to log out
    return Promise.resolve();
  },
  getIdentity: () => {
    const auth = Cookies.get(rednaxelCookie) ?? "";
    const decoded = jwtDecode<CustomJwtPayload>(auth);
    // console.log("auth", auth);
    // console.log("decoded", decoded);

    return Promise.resolve({
      id: decoded.cod_vendedor ?? 0,
      fullName: `${decoded.nome_usuario ?? "user"}@${decoded.conta ?? "tenant"}`,
      avatar: decoded.avatar,
      homologacao: decoded.homologacao ?? false,
      conta: decoded.conta,
      id_empresa: decoded.id_empresa,
      nome_reduzido: decoded.nome_reduzido,
      id_predio: decoded.id_predio,
      sigla_loja: decoded.sigla_loja,
      multi_empresa: decoded.multi_empresa ?? false,
      admin_scm: decoded.admin_scm ?? false,
      userID: parseInt(decoded.sub ?? "0") ?? undefined,
    });
  },
  getPermissions: () => {
    const auth = Cookies.get(rednaxelCookie) ?? "";
    const decoded = jwtDecode<CustomJwtPayload>(auth);
    return Promise.resolve(decoded.admin_scm ? "admin" : "user");
  },
};

export async function getIsInitialized() {
  return true;
}
